import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import useAddressPredictions from 'hooks/useAddressPredictions';
import {
	checkAddressByGooglePlace,
	checkAddressByGooglePlaceForModal,
} from 'reducers/addressReducer';

import './GoogleAddressSearchV2.css';

/**
 * The place marker shown against each suggestion.
 *
 * Drawn inline rather than pulled from Google's sprite sheet, which the old
 * widget used and which is not available to a list the application renders
 * itself.
 *
 * @returns {JSX.Element} The marker icon
 */
function PlaceIcon() {
	return (
		<svg
			className="google-address-search-v2-icon"
			viewBox="0 0 24 24"
			width="16"
			height="16"
			aria-hidden="true"
			focusable="false"
		>
			<path
				fill="currentColor"
				d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"
			/>
		</svg>
	);
}

/**
 * The bold first line of a suggestion, e.g. "54 Noll Street".
 *
 * Google supplies the split in structured_formatting. The full description is
 * used as a fallback so a prediction missing that field still renders.
 *
 * @param {Object} prediction A Google autocomplete prediction
 * @returns {string} The primary line
 */
export function mainText(prediction) {
	return prediction?.structured_formatting?.main_text || prediction?.description || '';
}

/**
 * The lighter trailing line of a suggestion, e.g. "Brooklyn, NY, USA".
 *
 * @param {Object} prediction A Google autocomplete prediction
 * @returns {string} The secondary line, empty when Google supplies none
 */
export function secondaryText(prediction) {
	return prediction?.structured_formatting?.secondary_text || '';
}

/**
 * Resolves the address to commit for one prediction.
 *
 * Ordinarily this returns Google's own description untouched -- the exact
 * value every selection has always committed. secondary_text normally holds
 * just "city, state zip" (at most two parts once the trailing country is
 * dropped). For an address next to a named premise (e.g. a hotel occupying
 * the building), Google inserts that name ahead of them instead --
 * "Embassy SUITE, New York, NY 10018" -- which is the one shape this checks
 * for: only then is a cleaned "street, city, state zip" rebuilt from
 * main_text plus secondary_text's last two parts, dropping the premise name
 * that isn't part of the address. Every ordinary selection never reaches
 * that branch, so it is not just behaviourally unchanged -- it runs the same
 * single line it always has.
 *
 * @param {Object} prediction A Google autocomplete prediction
 * @returns {string} The address to commit
 */
export function addressForPrediction(prediction) {
	const secondary = secondaryText(prediction);
	const parts = secondary
		.split(',')
		.map((part) => part.trim())
		.filter((part) => part && part.toUpperCase() !== 'USA');

	if (parts.length <= 2) {
		return prediction?.description || '';
	}

	const main = mainText(prediction);
	const tail = parts.slice(-2);

	return [main, ...tail].filter(Boolean).join(', ');
}

/**
 * Address search input backed by Google address predictions.
 *
 * A drop-in alternative to GoogleAddressSearch, which is left in place and
 * untouched. Both components take the same props and honour the same onSelect
 * contract, and each carries its own namespaced stylesheet, so a screen can be
 * pointed at either one by changing only its import -- and pointed back just as
 * easily if anything misbehaves.
 *
 * What differs is the cost of using it. GoogleAddressSearch mounts Google's own
 * Autocomplete widget, which issues a billable prediction request on every
 * keystroke and, on each selection, additionally fetches Place Details -- the
 * most expensive Places SKU. The app reads exactly one field of that response,
 * the formatted address, which every prediction already carries; the premium
 * call was bought and discarded.
 *
 * This component requests predictions directly, so the request rate is
 * controlled (see useAddressPredictions: a minimum query length, a debounce and
 * a session token), and a selection is resolved from the prediction already in
 * hand, so Place Details is never called at all.
 */
function GoogleAddressSearchV2(props) {
	const dispatch = useDispatch();

	let { label, hideLabel, placeholder, showHeaderHint } = props;
	label = label || 'Address Search';
	placeholder = placeholder || 'Find an Address';

	const [value, setValue] = useState('');
	const [open, setOpen] = useState(false);
	const [highlighted, setHighlighted] = useState(-1);

	const containerRef = useRef(null);
	const inputRef = useRef(null);

	// The hook only sees the query while the suggestion list is open. Committing
	// a selection writes the full chosen address into the input and closes the
	// list; if that write reached the hook it would debounce and issue one more
	// billable prediction request for an address the user has already chosen.
	// Gating on `open` also cancels the pending request when the user clicks
	// away mid-search, which would otherwise fire for a list nobody will see.
	const { predictions, ready, clear, startNewSession, endSession } =
		useAddressPredictions(open ? value : '');

	useEffect(() => {
		setHighlighted(-1);
	}, [predictions]);

	// Close the list when the focus or the pointer leaves the component, so a
	// stale dropdown never sits over the page.
	useEffect(() => {
		const onDocumentPointerDown = (event) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', onDocumentPointerDown);
		return () =>
			document.removeEventListener('mousedown', onDocumentPointerDown);
	}, []);

	const onPlaceSelected = useCallback(
		(place, inputValue) => {
			if (props?.isSearching) {
				props.isSearching(true);
			}
			if (props.onSelect) {
				dispatch(
					checkAddressByGooglePlaceForModal(place, inputValue, (result) => {
						props.onSelect(result);
					}),
				);
			} else {
				dispatch(checkAddressByGooglePlace(place, inputValue));
			}
		},
		[dispatch, props],
	);

	/**
	 * Commits one prediction as the chosen address.
	 *
	 * addressForPrediction() returns the prediction's own description
	 * untouched for every ordinary selection -- the same formatted address
	 * the Place Details response used to supply -- so it is handed to the
	 * reducer in the shape the reducer already reads.
	 *
	 * @param {Object} prediction The prediction the user chose
	 * @returns {void}
	 */
	const selectPrediction = useCallback(
		(prediction) => {
			if (!prediction) {
				return;
			}

			const address = addressForPrediction(prediction);

			setValue(address);
			setOpen(false);
			clear();
			// The session that led to this selection is finished; the next search
			// starts its own.
			endSession();

			onPlaceSelected({ formatted_address: address }, address);
		},
		[clear, endSession, onPlaceSelected],
	);

	const onChange = (event) => {
		const next = event.target.value;

		// A search beginning after a previous selection opens a new session.
		if (!value) {
			startNewSession();
		}

		setValue(next);
		setOpen(true);
	};

	const onKeyDown = (event) => {
		if (!open || predictions.length === 0) {
			return;
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setHighlighted((current) => (current + 1) % predictions.length);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setHighlighted((current) =>
				current <= 0 ? predictions.length - 1 : current - 1,
			);
		} else if (event.key === 'Enter') {
			if (highlighted >= 0) {
				event.preventDefault();
				selectPrediction(predictions[highlighted]);
			}
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	};

	const showList = open && predictions.length > 0;

	return (
		<div className="google-address-search-v2" ref={containerRef}>
			{!hideLabel && <label className="form-label">{label}</label>}
			<input
				ref={inputRef}
				type="text"
				className={`form-control ${showHeaderHint ? 'header-hint' : ''}`}
				placeholder={placeholder}
				autoFocus={showHeaderHint}
				autoComplete="off"
				value={value}
				onChange={onChange}
				onKeyDown={onKeyDown}
				onFocus={() => predictions.length > 0 && setOpen(true)}
				role="combobox"
				aria-expanded={showList}
				aria-autocomplete="list"
				aria-controls="google-address-search-v2-list"
				disabled={props.disabled}
			/>
			{showList && (
				<ul
					className="google-address-search-v2-list"
					id="google-address-search-v2-list"
					role="listbox"
				>
					{predictions.map((prediction, index) => (
						<li
							key={prediction.place_id || prediction.description}
							role="option"
							aria-selected={index === highlighted}
							className={`google-address-search-v2-option ${
								index === highlighted ? 'is-highlighted' : ''
							}`}
							// mousedown fires before the input's blur, so the click is not
							// lost to the dropdown closing first.
							onMouseDown={(event) => {
								event.preventDefault();
								selectPrediction(prediction);
							}}
							onMouseEnter={() => setHighlighted(index)}
						>
							<PlaceIcon />
							<span className="google-address-search-v2-text">
								<span className="google-address-search-v2-main">
									{mainText(prediction)}
								</span>
								{secondaryText(prediction) && (
									<span className="google-address-search-v2-secondary">
										{secondaryText(prediction)}
									</span>
								)}
							</span>
						</li>
					))}
					{/* Google's terms require this attribution whenever Places
					    predictions are shown outside a Google map, which is the
					    case here: the list is rendered by the application. */}
					<li
						className="google-address-search-v2-attribution"
						role="presentation"
						aria-hidden="true"
					>
						powered by <span className="google-address-search-v2-google">Google</span>
					</li>
				</ul>
			)}
			{!ready && (
				<input type="hidden" data-testid="google-places-unavailable" />
			)}
		</div>
	);
}

export default GoogleAddressSearchV2;
